import { LoadingContent, Spinner } from '../components/feedback';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { HomepageSettingsSchema, type HomepageImage, type HomepageSettings } from '@mcpanel/core';
import type { ServerView } from '../api/types';
import { HomepageImages } from './HomepageImages';
import { HomepageServers } from './HomepageServers';
import { api } from '../api/client';
import { errorMessage, useApp } from '../app-context';
import { ErrorAlert } from '../components/ui';

export function HomepageSection() {
  const { me } = useApp();
  return me?.user?.role === 'owner' ? <HomepageForm /> : null;
}

function HomepageForm() {
  const { notify } = useApp();
  const [settings, setSettings] = useState<HomepageSettings>();
  const [error, setError] = useState<Error>();
  const [busy, setBusy] = useState(false);
  const [images, setImages] = useState<HomepageImage[]>([]);
  const [servers, setServers] = useState<ServerView[]>([]);
  const reloadImages = useCallback(() => {
    void api
      .homepageImages()
      .then(setImages)
      .catch((err: unknown) => setError(new Error(errorMessage(err))));
  }, []);
  useEffect(() => {
    let active = true;
    reloadImages();
    void api
      .servers()
      .then((value) => {
        if (active) setServers(value);
      })
      .catch((err: unknown) => {
        if (active) setError(new Error(errorMessage(err)));
      });
    void api
      .homepage()
      .then((value) => {
        if (active) setSettings(value);
      })
      .catch((err: unknown) => {
        if (active) setError(new Error(errorMessage(err)));
      });
    return () => {
      active = false;
    };
  }, [reloadImages]);
  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      setSettings(await api.setHomepage(HomepageSettingsSchema.parse(settings)));
      notify('ホームページの設定を保存しました');
    } catch (err) {
      notify(errorMessage(err), true);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <div className="card-header">
        <h2 data-search-id="homepage">ホームページ</h2>
      </div>
      <form className="card-body stack" onSubmit={(event) => void save(event)}>
        <p className="small muted">
          ドメイン直下に表示する公開ページです。保存した内容はすぐ反映されます。公開対象は各サーバーのランチャー公開設定で選びます。
        </p>
        <ErrorAlert error={error} />
        <LoadingContent loading={!settings && !error} shape="settings">
          {settings && (
            <>
              <label className="field" data-search-id="homepage-name">
                表示する名前
                <input
                  className="input"
                  required
                  maxLength={100}
                  value={settings.name}
                  onChange={(e) => setSettings({ ...settings, name: e.target.value })}
                />
              </label>
              <label className="field" data-search-id="homepage-description">
                短い説明
                <textarea
                  className="input"
                  maxLength={500}
                  value={settings.description}
                  onChange={(e) => setSettings({ ...settings, description: e.target.value })}
                />
              </label>
              <label className="field" data-search-id="homepage-discord">
                Discord の招待リンク
                <input
                  className="input"
                  type="url"
                  maxLength={500}
                  placeholder="https://discord.gg/..."
                  value={settings.discordInviteUrl}
                  onChange={(e) => setSettings({ ...settings, discordInviteUrl: e.target.value })}
                />
              </label>
              <div className="stack">
                <h3 data-search-id="homepage-hero">背景の画像</h3>
                <p className="small muted">
                  トップの大きな画像です。複数あると 7
                  秒ごとに切り替わります。写真の位置情報などは保存の前に取り除きます。
                </p>
                <HomepageImages target="hero" label="背景の画像" images={images} reload={reloadImages} />
              </div>
              <HomepageServers
                servers={servers}
                settings={settings}
                onChange={(value) => setSettings({ ...settings, servers: value })}
                images={images}
                reloadImages={reloadImages}
              />
              <label className="field" data-search-id="homepage-rules">
                ルール（/rules のページ。「## 見出し」「* 箇条書き」が使えます）
                <textarea
                  className="input"
                  rows={16}
                  maxLength={20000}
                  value={settings.rules}
                  onChange={(e) => setSettings({ ...settings, rules: e.target.value })}
                />
              </label>
              <p className="small muted">画像はすぐ反映されます。文字の変更は「保存」を押すと反映されます。</p>
              <button className="btn primary" disabled={busy}>
                {busy && <Spinner />}
                保存
              </button>
            </>
          )}
        </LoadingContent>
      </form>
    </section>
  );
}
